import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Bell,
  Inbox,
  Mail,
  Loader2,
} from "lucide-react";
import {
  getNotificationPreferences,
  updateNotificationPreferences,
} from "../../api/system/notifications";
import {
  showErrorToast,
  showSuccessToast,
} from "../../components/ConfirmDialog";
import {
  MobilePageShell,
  MobilePageHeader,
  MobileSectionHeader,
  MobileNavGroup,
  MobileNavRow,
} from "./MobileNavPrimitives";

function ToggleSwitch({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative h-7 w-12 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
        checked ? "bg-red-700" : "bg-gray-200"
      }`}
    >
      <span
        className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${
          checked ? "left-5" : "left-0.5"
        }`}
      />
    </button>
  );
}

export default function NotificationSettingsScreen() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [email, setEmail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getNotificationPreferences();
      setNotifyEmail(Boolean(data?.notify_email));
      setEmail(data?.email || null);
    } catch (err) {
      console.error(err);
      showErrorToast(
        err?.response?.data?.message || "Không tải được tùy chọn thông báo"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleEmail = async (next) => {
    if (!email) {
      showErrorToast("Tài khoản chưa có email — không thể bật gửi email");
      return;
    }
    const prev = notifyEmail;
    setNotifyEmail(next);
    setSaving(true);
    try {
      const data = await updateNotificationPreferences({ notify_email: next });
      setNotifyEmail(Boolean(data?.notify_email));
      setEmail(data?.email || email);
      showSuccessToast(
        next
          ? "Đã bật nhận thông báo qua email"
          : "Đã tắt nhận thông báo qua email"
      );
    } catch (err) {
      setNotifyEmail(prev);
      showErrorToast(
        err?.response?.data?.message || "Không lưu được tùy chọn"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <MobilePageShell>
      <MobilePageHeader
        title="Cài đặt thông báo"
        subtitle="Kênh nhận thông báo hệ thống"
        tone="emerald"
        trailing={
          <button
            type="button"
            onClick={() => navigate("/m/settings")}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-100 bg-emerald-50 text-emerald-700"
            aria-label="Quay lại"
          >
            <ArrowLeft size={18} />
          </button>
        }
      />

      <div className="space-y-5 px-5 pt-5 pb-4">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-400">
            <Loader2 className="animate-spin" size={18} />
            Đang tải...
          </div>
        ) : (
          <>
            <section>
              <MobileSectionHeader title="Hộp thư" tone="emerald" />
              <MobileNavGroup>
                <MobileNavRow
                  icon={Inbox}
                  label="Thông báo trong hệ thống"
                  hint="Xem hộp thư in-app"
                  tone="emerald"
                  onClick={() => navigate("/system/notifications")}
                />
              </MobileNavGroup>
            </section>

            <section>
              <MobileSectionHeader title="Kênh nhận" tone="sky" />
              <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm divide-y divide-gray-50">
                <div className="flex items-center gap-3 px-4 py-3.5">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                    <Bell size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800">
                      Thông báo trong app
                    </p>
                    <p className="text-[11px] text-gray-400">
                      Luôn bật — chuông & hộp thư hệ thống
                    </p>
                  </div>
                  <span className="rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-emerald-700">
                    Bật
                  </span>
                </div>

                <div className="flex items-center gap-3 px-4 py-3.5">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-700">
                    <Mail size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800">
                      Gửi thêm qua email
                    </p>
                    <p className="truncate text-[11px] text-gray-400">
                      {email
                        ? `Gửi tới ${email}`
                        : "Chưa có email trên hồ sơ"}
                    </p>
                  </div>
                  <ToggleSwitch
                    checked={notifyEmail}
                    onChange={handleToggleEmail}
                    disabled={saving || !email}
                  />
                </div>
              </div>
              <p className="mt-2 px-0.5 text-[11px] leading-relaxed text-gray-400">
                Khi bật, mỗi thông báo hệ thống gửi tới bạn cũng được gửi bản
                sao tới email hồ sơ (nếu hệ thống đã cấu hình SMTP).
              </p>
            </section>
          </>
        )}
      </div>
    </MobilePageShell>
  );
}
